import type { LabTest, TestParameter } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import {
  type ProcedureLevel,
  type ProcedureValues,
  appendStep,
  removeStep,
  replaceStep,
} from './procedure-form.logic';
import { idChoice, nameAndCodeOptions } from '#client/lib/form-choices';
import type { SetField } from '#client/hooks/useFormValues';

const LEVEL_OPTIONS: { value: ProcedureLevel; label: string }[] = [
  { value: 'test', label: 'A whole test' },
  { value: 'parameter', label: 'One analyte' },
];

interface FieldsProps {
  values: ProcedureValues;
  onFieldChange: SetField<ProcedureValues>;
}

interface CoverageCardProps extends FieldsProps {
  errors: Record<string, string>;
  level: ProcedureLevel;
  onLevelChange: (level: ProcedureLevel) => void;
  tests: readonly LabTest[];
  parameters: readonly TestParameter[];
}

/** The title, and the one test or analyte the procedure is attached to. */
export function ProcedureCoverageCard({
  values,
  onFieldChange,
  errors,
  level,
  onLevelChange,
  tests,
  parameters,
}: CoverageCardProps) {
  return (
    <Card title="What it covers">
      <TextField
        label="Title"
        value={values.title}
        error={errors.title}
        onChange={(event) => onFieldChange('title', event.target.value)}
        helpText='As the SOP is known on the bench — "ALP (Centronics)"'
        required
      />

      <Segmented
        label="Applies to"
        options={LEVEL_OPTIONS}
        value={level}
        onChange={onLevelChange}
      />
      <p className="c-field__help">
        A CBC procedure covers the whole sample. The ALP example is per-analyte, which is also the
        level its reagent volumes are recorded at.
      </p>

      {level === 'test' ? (
        <Select
          label="Lab test"
          options={nameAndCodeOptions(tests)}
          placeholder="Choose a test"
          value={idChoice(values.test_id)}
          error={errors.test_id}
          onChange={(choice) => onFieldChange('test_id', Number(choice))}
          required
        />
      ) : (
        <Select
          label="Parameter"
          options={nameAndCodeOptions(parameters)}
          placeholder="Choose a parameter"
          value={idChoice(values.parameter_id)}
          error={errors.parameter_id}
          onChange={(choice) => onFieldChange('parameter_id', Number(choice))}
          required
        />
      )}
    </Card>
  );
}

export function SampleAndMethodCard({ values, onFieldChange }: FieldsProps) {
  return (
    <Card title="Sample and method">
      <div className="c-form-grid">
        <TextField
          label="Method"
          value={values.method ?? ''}
          onChange={(event) => onFieldChange('method', event.target.value)}
          helpText="Kinetic, IFCC without pyridoxal phosphate"
        />
        <TextField
          label="Specimen"
          value={values.specimen ?? ''}
          onChange={(event) => onFieldChange('specimen', event.target.value)}
        />
        <TextField
          label="Container"
          value={values.container ?? ''}
          onChange={(event) => onFieldChange('container', event.target.value)}
        />
        <TextField
          label="Sample volume"
          value={values.sample_volume ?? ''}
          onChange={(event) => onFieldChange('sample_volume', event.target.value)}
          helpText="Drawn from the patient — not stock, so it is not a reagent recipe"
        />
        <TextField
          label="Storage"
          value={values.storage ?? ''}
          onChange={(event) => onFieldChange('storage', event.target.value)}
        />
        <TextField
          label="Incubation"
          value={values.incubation ?? ''}
          onChange={(event) => onFieldChange('incubation', event.target.value)}
          helpText="Read after 30 s at 37 °C"
        />
        <TextField
          label="Wavelength"
          value={values.wavelength ?? ''}
          onChange={(event) => onFieldChange('wavelength', event.target.value)}
        />
        <TextField
          label="Temperature"
          value={values.temperature ?? ''}
          onChange={(event) => onFieldChange('temperature', event.target.value)}
        />
      </div>

      <TextArea
        label="Principle"
        rows={3}
        value={values.principle ?? ''}
        onChange={(event) => onFieldChange('principle', event.target.value)}
      />
    </Card>
  );
}

interface StepsCardProps {
  steps: readonly string[];
  onStepsChange: (change: (current: string[]) => string[]) => void;
}

export function ProcedureStepsCard({ steps, onStepsChange }: StepsCardProps) {
  return (
    <Card
      title="Steps"
      subtitle="Numbered, in order"
      actions={
        <Button size="sm" onClick={() => onStepsChange(appendStep)}>
          Add a step
        </Button>
      }
    >
      <ol className="c-procedure-editor">
        {steps.map((step, index) => (
          // Index-keyed on purpose: steps are positional and every field is
          // controlled, so removing one shifts the values, never the wrong text.
          <li key={index}>
            <TextField
              label={`Step ${index + 1}`}
              hideLabel
              value={step}
              onChange={(event) =>
                onStepsChange((current) => replaceStep(current, index, event.target.value))
              }
            />
            {steps.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onStepsChange((current) => removeStep(current, index))}
              >
                Remove
              </Button>
            )}
          </li>
        ))}
      </ol>

      <p className="c-field__help">
        Reagent volumes do not go here. &ldquo;400 µL R1 + 100 µL R2&rdquo; belongs in the reagent
        recipes, where it drives the stock deduction and the cost per test; the procedure repeats
        what the bench does with it.
      </p>
    </Card>
  );
}

export function InterpretationCard({ values, onFieldChange }: FieldsProps) {
  return (
    <Card title="Interpretation">
      <TextArea
        label="Calculation"
        rows={2}
        value={values.calculation ?? ''}
        onChange={(event) => onFieldChange('calculation', event.target.value)}
        helpText="Documentation. The app does not evaluate it — the analyser already reported the result."
      />
      <TextArea
        label="Interferences and limitations"
        rows={3}
        value={values.interferences ?? ''}
        onChange={(event) => onFieldChange('interferences', event.target.value)}
      />
      <TextArea
        label="References"
        rows={2}
        value={values.references ?? ''}
        onChange={(event) => onFieldChange('references', event.target.value)}
      />
    </Card>
  );
}
